/**
 * 통계 API 서비스
 * Backend 통계 API 호출 함수
 */

import api from '../utils/api';
import {
  StudentOverallStats,
  StudentAttemptStatsResponse,
  WorkbookProblemStatsResponse,
  StudentProgressTrendResponse,
  AnswerTypeStatsResponse,
} from '../types/stats';

const STATS_BASE_URL = '/stats';

/**
 * 학생 전체 통계 조회
 *
 * @param studentId - 학생 ID
 * @param workbookName - 문제집명 (선택, 없으면 전체)
 * @returns 학생 전체 통계
 */
export const getStudentOverallStats = async (
  studentId: string,
  workbookName?: string
): Promise<StudentOverallStats> => {
  const params = workbookName ? { workbook_name: workbookName } : {};

  const response = await api.get<StudentOverallStats>(
    `${STATS_BASE_URL}/students/${studentId}/overall`,
    { params }
  );

  return response.data;
};

/**
 * 학생 시도별 통계 조회
 *
 * @param studentId - 학생 ID
 * @param workbookName - 문제집명 (선택)
 * @returns 시도별 통계 목록
 */
export const getStudentAttemptStats = async (
  studentId: string,
  workbookName?: string
): Promise<StudentAttemptStatsResponse> => {
  const params = workbookName ? { workbook_name: workbookName } : {};

  const response = await api.get<StudentAttemptStatsResponse>(
    `${STATS_BASE_URL}/students/${studentId}/by-attempt`,
    { params }
  );

  return response.data;
};

/**
 * 문제집 문제별 통계 조회
 *
 * @param workbookName - 문제집명
 * @returns 문제별 통계 목록
 */
export const getWorkbookProblemStats = async (
  workbookName: string
): Promise<WorkbookProblemStatsResponse> => {
  const response = await api.get<WorkbookProblemStatsResponse>(
    `${STATS_BASE_URL}/workbooks/${workbookName}/problems`
  );

  return response.data;
};

/**
 * 학생 진도 추이 조회
 *
 * @param studentId - 학생 ID
 * @param workbookName - 문제집명
 * @returns 진도 추이 데이터 (시간순)
 */
export const getStudentProgressTrend = async (
  studentId: string,
  workbookName: string
): Promise<StudentProgressTrendResponse> => {
  const response = await api.get<StudentProgressTrendResponse>(
    `${STATS_BASE_URL}/workbooks/${workbookName}/students/${studentId}/progress`
  );

  return response.data;
};

/**
 * 답형식별 통계 조회
 *
 * @param options - 필터 옵션 (학생 ID, 문제집명)
 * @returns 답형식별 통계
 */
export const getAnswerTypeStats = async (options?: {
  studentId?: string;
  workbookName?: string;
}): Promise<AnswerTypeStatsResponse> => {
  const params: Record<string, string> = {};

  if (options?.studentId) {
    params.student_id = options.studentId;
  }
  if (options?.workbookName) {
    params.workbook_name = options.workbookName;
  }

  const response = await api.get<AnswerTypeStatsResponse>(
    `${STATS_BASE_URL}/answer-types`,
    { params }
  );

  return response.data;
};
